// Composant Grille de reponses 2x2 — shadcn tokens
// Radio buttons avec pastille coloree + label + checkmark
// Couleurs sémantiques rouge→orange→ambre→vert (maturité 0→3)

import { useCallback } from 'react'
import { cn } from '../../lib/utils'

export type ReponseValeur = 0 | 1 | 2 | 3

export interface ResponseGridProps {
  value?: number
  onChange: (value: ReponseValeur) => void
  disabled?: boolean
}

const RESPONSES: Array<{
  value: ReponseValeur
  label: string
  color: string
  colorBg: string
  colorBorder: string
  colorRing: string
}> = [
  {
    value: 0,
    label: 'Non prévu',
    color: 'bg-red-500',
    colorBg: 'bg-red-50',
    colorBorder: 'border-red-200',
    colorRing: 'ring-red-500/30',
  },
  {
    value: 1,
    label: 'Planifié',
    color: 'bg-orange-500',
    colorBg: 'bg-orange-50',
    colorBorder: 'border-orange-200',
    colorRing: 'ring-orange-500/30',
  },
  {
    value: 2,
    label: 'En cours',
    color: 'bg-amber-500',
    colorBg: 'bg-amber-50',
    colorBorder: 'border-amber-200',
    colorRing: 'ring-amber-500/30',
  },
  {
    value: 3,
    label: 'En place',
    color: 'bg-green-500',
    colorBg: 'bg-green-50',
    colorBorder: 'border-green-200',
    colorRing: 'ring-green-500/30',
  },
]

export default function ResponseGrid({
  value,
  onChange,
  disabled = false,
}: ResponseGridProps) {
  const handleSelect = useCallback(
    (selectedValue: ReponseValeur) => {
      if (!disabled) {
        onChange(selectedValue)
      }
    },
    [disabled, onChange]
  )

  return (
    <div className="grid grid-cols-2 gap-3">
      {RESPONSES.map((response) => {
        const isSelected = value === response.value

        return (
          <button
            key={response.value}
            type="button"
            disabled={disabled}
            onClick={() => handleSelect(response.value)}
            className={cn(
              "relative flex items-center gap-3 p-4 rounded-xl",
              "border-2 transition-all duration-200",
              "focus:outline-none focus:ring-2 focus:ring-offset-2",
              response.colorRing,
              "disabled:opacity-50 disabled:cursor-not-allowed",
              isSelected
                ? `${response.colorBg} ${response.colorBorder} shadow-sm`
                : "bg-card border-border hover:border-muted-foreground/30 hover:bg-muted/50"
            )}
            aria-pressed={isSelected}
          >
            {/* Radio button avec pastille coloree */}
            <div className={cn(
              "flex-shrink-0 w-5 h-5 rounded-full",
              "border-2 flex items-center justify-center",
              "transition-all duration-200",
              isSelected
                ? `${response.colorBorder} ${response.color}`
                : "border-muted-foreground/30 bg-card"
            )}>
              {isSelected && (
                <div className="w-2 h-2 rounded-full bg-white" />
              )}
            </div>

            {/* Label */}
            <span className={cn(
              "text-sm font-medium text-left flex-1",
              isSelected ? "text-foreground" : "text-muted-foreground"
            )}>
              {response.label}
            </span>

            {/* Checkmark quand selectionne */}
            {isSelected && (
              <svg
                className="w-5 h-5 text-green-600 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </button>
        )
      })}
    </div>
  )
}
